import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, Send, Loader2 } from "lucide-react";
import { CONTACT } from "@/lib/site-data";
import { SectionHeading } from "@/components/SectionHeading";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOPICS = ["Fenster", "Haustür", "Rollläden", "Sonnenschutz", "Insektenschutz", "Wintergarten", "Vordach", "Schiebetür", "Garagentor", "Zaun", "Pergola", "Sonstiges"];

const INFO = [
  { icon: Phone, label: "Telefon", value: CONTACT.phone, href: `tel:${CONTACT.phoneRaw}` },
  { icon: Mail, label: "E-Mail", value: CONTACT.email, href: `mailto:${CONTACT.email}` },
  { icon: MapPin, label: "Adresse", value: CONTACT.address },
  { icon: Clock, label: "Öffnungszeiten", value: CONTACT.hours },
];

const inputCls =
  "w-full bg-white/5 border border-white/15 rounded-xl px-5 py-4 text-white placeholder:text-white/35 text-sm focus:outline-none focus:border-[#e2001a] focus:bg-white/10 transition-colors duration-200";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", topic: TOPICS[0], message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Vielen Dank! Ihre Anfrage ist eingegangen – wir melden uns innerhalb von 24 Stunden.");
      setForm({ name: "", email: "", phone: "", topic: TOPICS[0], message: "" });
    } catch {
      toast.error("Senden fehlgeschlagen. Bitte versuchen Sie es erneut oder rufen Sie uns an.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="kontakt" data-testid="contact-section" className="bg-[#14171a] py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-5 gap-14 lg:gap-20">
        <div className="lg:col-span-2">
          <SectionHeading
            dark
            testid="contact"
            eyebrow="Kontakt"
            title="Sprechen wir über Ihr Projekt."
            text="Ob Neubau, Sanierung oder einzelnes Fenster – schreiben Sie uns. Wir antworten in der Regel innerhalb von 24 Stunden."
          />
          <div className="mt-12 space-y-6">
            {INFO.map((item) => (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="flex items-start gap-4"
                data-testid={`contact-info-${item.label.toLowerCase()}`}
              >
                <span className="w-11 h-11 shrink-0 rounded-full bg-white/5 border border-white/15 flex items-center justify-center text-[#e2001a]">
                  <item.icon size={17} />
                </span>
                <div>
                  <div className="text-white/40 text-xs uppercase tracking-[0.2em] font-display">{item.label}</div>
                  {item.href ? (
                    <a href={item.href} className="text-white/85 hover:text-white text-sm sm:text-base mt-1 inline-block transition-colors duration-200">
                      {item.value}
                    </a>
                  ) : (
                    <div className="text-white/85 text-sm sm:text-base mt-1">{item.value}</div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <motion.form
          data-testid="contact-form"
          onSubmit={submit}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-3 bg-[#1e2226] border border-white/10 rounded-2xl p-7 sm:p-10 space-y-5"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <input data-testid="contact-name" required value={form.name} onChange={set("name")} placeholder="Ihr Name *" className={inputCls} />
            <input data-testid="contact-email" required type="email" value={form.email} onChange={set("email")} placeholder="E-Mail-Adresse *" className={inputCls} />
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <input data-testid="contact-phone" value={form.phone} onChange={set("phone")} placeholder="Telefon (optional)" className={inputCls} />
            <select data-testid="contact-topic" value={form.topic} onChange={set("topic")} className={`${inputCls} appearance-none cursor-pointer [&>option]:bg-[#1e2226]`}>
              {TOPICS.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <textarea
            data-testid="contact-message"
            required
            value={form.message}
            onChange={set("message")}
            placeholder="Ihre Nachricht *"
            rows={5}
            className={`${inputCls} resize-none`}
          />
          <button
            data-testid="contact-submit"
            type="submit"
            disabled={sending}
            className="inline-flex items-center gap-2.5 bg-[#e2001a] hover:bg-[#b80016] disabled:opacity-60 text-white font-display font-semibold px-9 py-4 rounded-full transition-colors duration-300"
          >
            {sending ? <Loader2 size={17} className="animate-spin" /> : <Send size={16} />}
            {sending ? "Wird gesendet …" : "Nachricht senden"}
          </button>
        </motion.form>
      </div>
    </section>
  );
};
